import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Copy, ExternalLink, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public Agent Landing Page editor (Round 38 · Item 5).
 * Saved config drives what /agent/{slug} renders. All fields optional except slug.
 */
const PublicPageEditor = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [specialtiesText, setSpecialtiesText] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/profile/public-page`, { withCredentials: true });
      setCfg(data);
      setSpecialtiesText((data.specialties || []).join(', '));
    } catch { toast.error('Could not load public-page config'); }
  };
  useEffect(() => { load(); }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/profile/logo`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setCfg(c => ({ ...c, logo_url: data.logo_url }));
      toast.success('Logo uploaded');
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Upload failed'); }
  };

  if (!cfg) return <p className="text-sm text-slate-500">Loading…</p>;

  const publicUrl = cfg.slug ? `${window.location.origin}/agent/${cfg.slug}` : '';

  const save = async () => {
    setSaving(true);
    try {
      const specialties = specialtiesText.split(',').map(s => s.trim()).filter(Boolean);
      const { data } = await axios.put(`${API}/profile/public-page`, {
        slug: cfg.slug || null,
        bio: cfg.bio, tagline: cfg.tagline, specialties,
        years_experience: cfg.years_experience ? Number(cfg.years_experience) : null,
        location: cfg.location,
        accent_color: cfg.accent_color || '#059669',
        show_photo: cfg.show_photo, show_phone: cfg.show_phone, show_email: cfg.show_email,
        active: cfg.active,
      }, { withCredentials: true });
      toast.success('Public page saved');
      setCfg(c => ({ ...c, slug: data.slug, public_url: data.public_url }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Slug (used in URL)</Label>
          <Input value={cfg.slug || ''} onChange={(e) => setCfg({ ...cfg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} placeholder="e.g. rahul-sharma" data-testid="public-page-slug" />
          <p className="text-[10px] text-slate-500">Lowercase letters, numbers, dashes. Leave blank to auto-generate.</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tagline</Label>
          <Input value={cfg.tagline || ''} onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })} placeholder="Your trusted insurance advisor" data-testid="public-page-tagline" />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">About / Bio</Label>
          <Textarea value={cfg.bio || ''} onChange={(e) => setCfg({ ...cfg, bio: e.target.value })} rows={3} maxLength={600} placeholder="A few lines about your experience and how you help clients…" data-testid="public-page-bio" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Specialties (comma-separated)</Label>
          <Input value={specialtiesText} onChange={(e) => setSpecialtiesText(e.target.value)} placeholder="Term Life, Health, Motor" data-testid="public-page-specialties" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Location</Label>
          <Input value={cfg.location || ''} onChange={(e) => setCfg({ ...cfg, location: e.target.value })} placeholder="Mumbai" data-testid="public-page-location" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Years of Experience</Label>
          <Input type="number" min="0" value={cfg.years_experience || ''} onChange={(e) => setCfg({ ...cfg, years_experience: e.target.value })} placeholder="10" data-testid="public-page-years" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Accent color (theme)</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={cfg.accent_color || '#059669'} onChange={(e) => setCfg({ ...cfg, accent_color: e.target.value })} className="h-9 w-14 rounded border border-slate-300 cursor-pointer" data-testid="public-page-accent" />
            <Input value={cfg.accent_color || '#059669'} onChange={(e) => setCfg({ ...cfg, accent_color: e.target.value })} placeholder="#059669" className="font-mono text-xs" data-testid="public-page-accent-hex" />
          </div>
          <p className="text-[10px] text-slate-500">Used on your public page header + Quotation PDFs.</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Logo (optional)</Label>
          <div className="flex items-center gap-2">
            {cfg.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${cfg.logo_url}`} alt="logo" className="h-9 w-9 object-contain rounded border border-slate-200 bg-white" data-testid="public-page-logo-preview" />}
            <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={uploadLogo} data-testid="public-page-logo-input" className="text-xs" />
          </div>
          <p className="text-[10px] text-slate-500">Shown on your public page + top-corner of Quotation PDFs. Max 2 MB.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['show_photo', 'Show photo'], ['show_phone', 'Show phone'], ['show_email', 'Show email'], ['active', 'Page is live']].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!cfg[key]} onChange={(e) => setCfg({ ...cfg, [key]: e.target.checked })} data-testid={`public-page-${key}`} className="h-4 w-4 accent-blue-800" />
            {label}
          </label>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="text-xs text-slate-500">
          Views: <strong className="text-slate-800">{cfg.views_count || 0}</strong> · Leads captured: <strong className="text-emerald-800">{cfg.leads_count || 0}</strong>
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="public-page-save">
          {saving ? 'Saving…' : 'Save Public Page'}
        </Button>
      </div>

      {publicUrl && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex flex-wrap items-center gap-2" data-testid="public-page-url-panel">
          <span className="text-xs text-emerald-900 font-medium">Your shareable URL:</span>
          <code className="text-xs bg-white px-2 py-1 rounded border border-emerald-200 break-all">{publicUrl}</code>
          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="public-page-copy">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-700" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}
          </Button>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-emerald-800 hover:underline inline-flex items-center gap-1" data-testid="public-page-open">
            <ExternalLink className="h-3 w-3" /> Preview
          </a>
        </div>
      )}
    </div>
  );
};

export default PublicPageEditor;
